import { LOCATIONS, type Location } from "@/content/locations";

/**
 * Bangla counterparts for the newest batch of city pages. Only cities listed
 * here get `/bn/seo-services/*` routes and reciprocal hreflang — pointing a
 * crawler at a Bangla URL that does not exist is worse than no alternate.
 *
 * Content is written natively in Bangla (not machine-translated) and mirrors
 * the English page structure section for section.
 */
export type BnLocation = {
  /** Shared slug with the English page — keeps the URL pair predictable. */
  slug: string;
  /** English city name, used for schema `alternateName` and the EN link. */
  city: string;
  cityBn: string;
  regionBn: string;
  districtBn: string;
  population: string;
  intro: string;
  industries: string[];
  neighborhoods: string[];
  competitors: string;
  why: string[];
  faq: { q: string; a: string }[];
};

export const BN_LOCATIONS: BnLocation[] = [
  {
    slug: "tangail",
    city: "Tangail",
    cityBn: "টাঙ্গাইল",
    regionBn: "ঢাকা বিভাগ",
    districtBn: "টাঙ্গাইল",
    population: "প্রায় ১,৮০,০০০ (শহর) / ৪০ লাখ+ (জেলা)",
    intro:
      "ঢাকা থেকে এক ঘণ্টার দূরত্বে টাঙ্গাইলের অর্থনীতি দাঁড়িয়ে আছে দুইটি জিনিসের ওপর — তাঁতশিল্প ও রাজধানীমুখী সেবা খাত। এখানকার ক্রেতারা মোবাইলে বাংলায় খোঁজেন এবং ফোন কলেই সিদ্ধান্ত নেন, তাই ব্লু লিংকের চেয়ে ম্যাপ প্যাকের গুরুত্ব বেশি।",
    industries: ["তাঁত ও টেক্সটাইল", "কৃষি ও এগ্রো-প্রসেসিং", "শিক্ষা ও কোচিং", "স্বাস্থ্যসেবা", "খুচরা ব্যবসা", "পরিবহন ও লজিস্টিক"],
    neighborhoods: ["শহীদ মিনার এলাকা", "ভিক্টোরিয়া রোড", "নিরালা মোড়", "কাগমারী", "সন্তোষ", "করটিয়া", "এলেঙ্গা", "মির্জাপুর"],
    competitors:
      "টাঙ্গাইলের সার্চ ফলাফল এখনো ডিরেক্টরি লিস্টিং আর দাবিহীন প্রোফাইলে ভরা। একটি ক্লেইম করা, গোছানো Google Business Profile এবং একটি বাংলা সার্ভিস পেজই বেশিরভাগ ক্যাটাগরিতে এগিয়ে দেয়।",
    why: [
      "শাড়ি ও তাঁত ব্যবসার জন্য ব্র্যান্ড নয়, ক্রেতার কীওয়ার্ডে র‍্যাংকিং।",
      "ক্লিনিক ও দোকানের জন্য Google Business Profile ক্লেইম ও রিভিউ সিস্টেম।",
      "টাঙ্গাইলের ক্রেতারা যেভাবে লেখেন, ঠিক সেভাবেই বাংলা কনটেন্ট।",
      "মহাসড়ক-কেন্দ্রিক ব্যবসা (হোটেল, পরিবহন, এলেঙ্গা এলাকা) লক্ষ্য করে টার্গেটিং।",
    ],
    faq: [
      { q: "টাঙ্গাইলে এসইও-র খরচ কেমন?", a: "লোকাল রিটেইনার সাধারণত মাসে ১৮,০০০–৪০,০০০ টাকা। শুরু হিসেবে অনেকে Google Business Profile সেটআপ ও একটি স্টার্টার অডিট নেন, যেটির খরচ অনেক কম।" },
      { q: "টাঙ্গাইলের শাড়ি ও তাঁত ব্যবসা কি অনলাইনে বিক্রি বাড়াতে পারে?", a: "পারে। ক্রেতার সার্চ টার্ম ধরে প্রোডাক্ট ও ক্যাটাগরি পেজ, সঙ্গে ফেসবুক থেকে সাইটে ফানেল — মার্কেটপ্লেসের ওপর নির্ভরতা ছাড়াই বিক্রি হয়।" },
      { q: "ফল পেতে কত সময় লাগে?", a: "প্রতিযোগিতা কম বলে ম্যাপ প্যাকে নড়াচড়া প্রায়ই ৩০–৬০ দিনেই দেখা যায়, আর অর্গানিক ট্রাফিকে স্পষ্ট পরিবর্তন ৩–৪ মাসে।" },
      { q: "মির্জাপুর, কালিহাতী বা এলেঙ্গা কি কভার করেন?", a: "হ্যাঁ। যেসব উপজেলায় বাস্তব সার্চ ডিমান্ড আছে, সেগুলোর জন্য আলাদা পেজ তৈরি করি।" },
      { q: "কাজ কি বাংলায় হয়?", a: "হ্যাঁ। এখানকার বেশিরভাগ সার্চ বাংলা বা বাংলিশে হয়, তাই কীওয়ার্ড পরিকল্পনা বাংলা দিয়েই শুরু হয়।" },
    ],
  },
  {
    slug: "feni",
    city: "Feni",
    cityBn: "ফেনী",
    regionBn: "চট্টগ্রাম বিভাগ",
    districtBn: "ফেনী",
    population: "প্রায় ১,৬০,০০০ (শহর) / ১৬ লাখ+ (জেলা)",
    intro:
      "ঢাকা–চট্টগ্রাম মহাসড়কের পাশে ফেনী একটি রেমিট্যান্স-নির্ভর জেলা — আয়তনের তুলনায় জমি, বেসরকারি স্বাস্থ্যসেবা ও শিক্ষায় খরচ অনেক বেশি। প্রবাসী পরিবার ইংরেজিতে খোঁজেন, দেশের ক্রেতা বাংলায়; জিততে হলে দুটোই কভার করতে হয়।",
    industries: ["জমি ও আবাসন", "বেসরকারি স্বাস্থ্যসেবা", "শিক্ষা ও কোচিং", "রেমিট্যান্স ও আর্থিক সেবা", "খুচরা ব্যবসা", "পরিবহন"],
    neighborhoods: ["ট্রাংক রোড", "মহিপাল", "শহীদ শহীদুল্লা কায়সার রোড", "সুলতানপুর", "দাগনভূঞা", "ছাগলনাইয়া", "সোনাগাজী", "পাঠান বাড়ি রোড"],
    competitors:
      "ফেনীর প্রায় কোনো ব্যবসাই স্কিমা মার্কআপ বা নিয়মিত রিভিউ প্রোগ্রাম চালায় না। ধারাবাহিক NAP সাইটেশন আর একটি দ্রুতগতির বাংলা ল্যান্ডিং পেজই বর্তমান প্রতিযোগীদের ছাড়িয়ে যায়।",
    why: [
      "মধ্যপ্রাচ্য ও মালয়েশিয়া থেকে খোঁজা প্রবাসী ক্রেতাদের জন্য দ্বিভাষিক টার্গেটিং।",
      "প্রকল্প ও এলাকাভিত্তিক ইনটেন্ট ধরে আবাসন পেজ।",
      "প্রাইভেট ক্লিনিক ও ডায়াগনস্টিক সেন্টারের ম্যাপ প্যাক অপটিমাইজেশন।",
      "মহাসড়ক-কেন্দ্রিক পরিবহন ও আতিথেয়তা ব্যবসার দৃশ্যমানতা।",
    ],
    faq: [
      { q: "ফেনীতে এসইও-র খরচ কত?", a: "সাধারণত মাসে ২০,০০০–৪৫,০০০ টাকা থেকে শুরু; আবাসন ও স্বাস্থ্যসেবার ক্যাম্পেইন এর ওপরের দিকে থাকে।" },
      { q: "প্রবাসী ফেনীবাসীদের কাছে পৌঁছানো যাবে?", a: "যাবে। মধ্যপ্রাচ্য, মালয়েশিয়া ও যুক্তরাজ্যের ইনটেন্ট ধরে ইংরেজি পেজ, আর দেশের ক্রেতার জন্য বাংলা পেজ — দুটো একসঙ্গে চলে।" },
      { q: "ফেনীর ডেভেলপারদের সঙ্গে কাজ করেন?", a: "হ্যাঁ। প্রকল্প পেজ, এলাকা গাইড ও লিড ফর্ম — শুধু ট্রাফিক নয়, অনুসন্ধান ধরার জন্য তৈরি।" },
      { q: "ফল পেতে কত সময়?", a: "ম্যাপ প্যাকে সাধারণত ৩০–৬০ দিন; প্রতিযোগিতামূলক জমি ও স্বাস্থ্যসেবার টার্মে ৩–৫ মাস।" },
      { q: "দাগনভূঞা, ছাগলনাইয়া, সোনাগাজী কি অন্তর্ভুক্ত?", a: "হ্যাঁ — সার্চ ডিমান্ড থাকলে উপজেলা পেজও তৈরি করা হয়।" },
    ],
  },
  {
    slug: "noakhali",
    city: "Noakhali",
    cityBn: "নোয়াখালী",
    regionBn: "চট্টগ্রাম বিভাগ",
    districtBn: "নোয়াখালী",
    population: "প্রায় ১,২০,০০০ (মাইজদী) / ৩৬ লাখ+ (জেলা)",
    intro:
      "নোয়াখালীর অর্থনীতি চলে রেমিট্যান্স, মৎস্য, কৃষি আর মাইজদীকেন্দ্রিক বিশ্ববিদ্যালয় শহরের ওপর ভর করে। সার্চ ভলিউম বিশাল নয়, কিন্তু ইনটেন্ট শক্তিশালী — আর জেলায় কেউই এখনো গুরুত্ব দিয়ে প্রতিযোগিতা করছে না।",
    industries: ["মৎস্য ও এগ্রো-প্রসেসিং", "শিক্ষা ও বিশ্ববিদ্যালয় সেবা", "স্বাস্থ্যসেবা", "রেমিট্যান্স ও আর্থিক সেবা", "খুচরা ব্যবসা", "নির্মাণ সামগ্রী"],
    neighborhoods: ["মাইজদী কোর্ট", "সোনাপুর", "চৌমুহনী", "বেগমগঞ্জ", "কোম্পানীগঞ্জ", "হাতিয়া", "সুবর্ণচর", "দত্তেরহাট"],
    competitors:
      "এখানে প্রতিযোগিতা মানে মূলত দাবিহীন Google প্রোফাইল আর ফেসবুক পেজ। স্কিমা, সাইটেশন ও রিভিউসহ একটি ঠিকঠাক সাইট দ্রুতই ক্যাটাগরির শীর্ষে চলে যায়।",
    why: [
      "নোয়াখালী বিজ্ঞান ও প্রযুক্তি বিশ্ববিদ্যালয়কেন্দ্রিক চাহিদা ধরে টার্গেটিং।",
      "পাইকারি ক্রেতাদের জন্য মৎস্য ও এগ্রো-প্রসেসিং B2B পেজ।",
      "ক্লিনিক ও দোকানের প্রোফাইল ক্লেইম এবং রিভিউ গতি বাড়ানো।",
      "বাংলা-প্রথম কীওয়ার্ড কৌশল, সঙ্গে প্রবাসীদের জন্য ইংরেজি পেজ।",
    ],
    faq: [
      { q: "নোয়াখালীতে এসইও-র খরচ কেমন?", a: "লোকাল প্যাকেজ সাধারণত মাসে ১৫,০০০–৩৫,০০০ টাকা — বেশিরভাগ ক্যাটাগরিতে জেলায় শীর্ষে যাওয়ার জন্য এটাই যথেষ্ট।" },
      { q: "নোয়াখালীতে কি এসইও করার মতো যথেষ্ট সার্চ আছে?", a: "লোকাল সেবার জন্য আছে। ভলিউম মাঝারি, কিন্তু ইনটেন্ট শক্ত এবং প্রতিযোগিতা কম বলে প্রতি লিডের খরচ অনেক কম থাকে।" },
      { q: "চৌমুহনী ও বেগমগঞ্জের ব্যবসা কি অন্তর্ভুক্ত?", a: "হ্যাঁ। বিশেষ করে চৌমুহনীতে বাস্তব বাণিজ্যিক সার্চ ডিমান্ড আছে, মাইজদীর সঙ্গে সেটিও কভার করা হয়।" },
      { q: "ফল পেতে কত সময় লাগে?", a: "বাজার এতটাই কম-অপটিমাইজড যে ম্যাপ প্যাকে পরিবর্তন প্রায়ই ৩০–৪৫ দিনেই দেখা যায়।" },
      { q: "মৎস্য ও পাইকারি সরবরাহকারীদের সাহায্য করেন?", a: "হ্যাঁ — শুধু স্থানীয় নয়, ঢাকা ও চট্টগ্রামের ক্রেতাদের লক্ষ্য করে B2B পেজ তৈরি করি।" },
    ],
  },
  {
    slug: "kushtia",
    city: "Kushtia",
    cityBn: "কুষ্টিয়া",
    regionBn: "খুলনা বিভাগ",
    districtBn: "কুষ্টিয়া",
    population: "প্রায় ১,৫০,০০০ (শহর) / ২১ লাখ+ (জেলা)",
    intro:
      "কুষ্টিয়ায় একদিকে চিনি, তামাক ও চালকল ঘিরে ভারী এগ্রো-শিল্প, অন্যদিকে ইসলামী বিশ্ববিদ্যালয়ঘেঁষা বিশাল শিক্ষার্থী বাজার। B2B ক্রেতা আর সাধারণ ক্রেতা একদম আলাদাভাবে খোঁজেন — একটি সাইট দিয়ে দুটোই সামলানো সহজ নয়।",
    industries: ["এগ্রো-প্রসেসিং ও চালকল", "চিনি ও তামাক", "শিক্ষা ও কোচিং", "স্বাস্থ্যসেবা", "হস্তশিল্প ও সংস্কৃতি পর্যটন", "খুচরা ব্যবসা"],
    neighborhoods: ["এনএস রোড", "মজমপুর", "থানাপাড়া", "কুমারখালী", "ভেড়ামারা", "খোকসা", "শিলাইদহ", "চৌড়হাস"],
    competitors:
      "কুষ্টিয়ার বেশিরভাগ শিল্প-সরবরাহকারীর কোনো ওয়েবসাইটই নেই, ব্যবসা চলে ফোন-রেফারেলে। একটি গোছানো প্রোডাক্ট ক্যাটালগই পুরো জেলাকে ছাড়িয়ে যায়।",
    why: [
      "চালকল, এগ্রো-প্রসেসর ও যন্ত্রপাতি সরবরাহকারীদের জন্য B2B ক্যাটালগ এসইও।",
      "ইসলামী বিশ্ববিদ্যালয়ঘেঁষা কোচিং ও হোস্টেলের জন্য শিক্ষার্থী-ইনটেন্ট কনটেন্ট।",
      "শিলাইদহ ও লালনকেন্দ্রিক পর্যটন চাহিদার জন্য আলাদা পেজ।",
      "পুরো জেলায় পরিচ্ছন্ন সাইটেশনসহ বাংলা-প্রথম লোকাল এসইও।",
    ],
    faq: [
      { q: "কুষ্টিয়ায় এসইও-র খরচ কত?", a: "লোকাল রিটেইনার সাধারণত মাসে ১৮,০০০–৪০,০০০ টাকা; ভারী কনটেন্টসহ B2B ক্যাটালগের কাজ এর ওপরে।" },
      { q: "এগ্রো-শিল্প সরবরাহকারীদের সঙ্গে কাজ করেন?", a: "হ্যাঁ — ঢাকা, খুলনা ও বিদেশি ক্রয়-বিভাগের জন্য লেখা প্রোডাক্ট ও সক্ষমতা পেজ।" },
      { q: "বিশ্ববিদ্যালয়ের পাশের কোচিং সেন্টারদের সাহায্য করেন?", a: "হ্যাঁ — কোর্স পেজ, ভর্তি মৌসুমের কনটেন্ট এবং শিক্ষার্থীদের সার্চ আচরণ ধরে রিভিউ সিস্টেম।" },
      { q: "ফল পেতে কত সময়?", a: "ম্যাপ প্যাকে ৩০–৬০ দিন, আর চতুর্থ মাস নাগাদ অর্গানিকে স্থায়ী উন্নতি আশা করা যায়।" },
      { q: "কুমারখালী ও ভেড়ামারা কি কভার করা হয়?", a: "হ্যাঁ — বাস্তব চাহিদা থাকলে উপজেলা পেজ যুক্ত করা হয়।" },
    ],
  },
  {
    slug: "nilphamari",
    city: "Nilphamari",
    cityBn: "নীলফামারী",
    regionBn: "রংপুর বিভাগ",
    districtBn: "নীলফামারী",
    population: "প্রায় ১,১০,০০০ (শহর) / ২০ লাখ+ (জেলা)",
    intro:
      "নীলফামারী আমার নিজের বিভাগের চুপচাপ বেড়ে ওঠা গল্প — উত্তরা ইপিজেড, পাশেই সৈয়দপুরের হালকা প্রকৌশল কারখানা, আর ধীরে ধীরে অনলাইনে আসা কৃষি অর্থনীতি। এই জেলায় আমি স্প্রেডশিট থেকে নয়, সরাসরি গিয়ে কাজ করি।",
    industries: ["পোশাক ও ইপিজেড উৎপাদন", "হালকা প্রকৌশল", "কৃষি", "স্বাস্থ্যসেবা", "শিক্ষা", "খুচরা ব্যবসা"],
    neighborhoods: ["নীলফামারী সদর", "উত্তরা ইপিজেড", "ডোমার", "ডিমলা", "জলঢাকা", "কিশোরগঞ্জ উপজেলা", "সোনারায়", "বড় বাজার"],
    competitors:
      "কয়েকটি ইপিজেড সরবরাহকারী ছাড়া এখানে এসইও প্রতিযোগিতা প্রায় শূন্য। প্রোফাইল ক্লেইম করে সত্যিকারের সার্ভিস পেজ প্রকাশ করলেই জেলায় শীর্ষে থাকা যায়।",
    why: [
      "রংপুরে থাকি বলে সরাসরি গিয়ে মিটিং ও প্রোফাইলের ছবি তোলা সম্ভব।",
      "ইপিজেড সরবরাহকারী ও হালকা প্রকৌশলের জন্য ক্রয়-ইনটেন্ট পেজ।",
      "মৌসুমি চাহিদা ধরে কৃষি ও কৃষি-উপকরণ কনটেন্ট।",
      "উত্তরাঞ্চলের সার্চ আচরণ অনুযায়ী বাংলা-প্রথম কীওয়ার্ড কৌশল।",
    ],
    faq: [
      { q: "নীলফামারীতে এসইও-র খরচ কেমন?", a: "প্যাকেজ সাধারণত মাসে ১৫,০০০–৩২,০০০ টাকা থেকে শুরু — কম প্রতিযোগিতার বাজারে এই বাজেটেই অনেক দূর যাওয়া যায়।" },
      { q: "নীলফামারীতে সরাসরি এসে কাজ করেন?", a: "হ্যাঁ। আমি রংপুরে থাকি, প্রায় এক ঘণ্টার পথ — তাই সাইট ভিজিট ও প্রোফাইলের ছবি তোলা বাস্তবসম্মত।" },
      { q: "উত্তরা ইপিজেডের সরবরাহকারীদের সাহায্য করেন?", a: "হ্যাঁ — ইপিজেড থেকে সোর্সিং করা ক্রেতাদের জন্য সক্ষমতা ও কমপ্লায়েন্স পেজ।" },
      { q: "ফল পেতে কত সময় লাগে?", a: "প্রতিযোগী অপটিমাইজড না হওয়ায় ম্যাপ প্যাকের ফল প্রায়ই ৩০–৪৫ দিনেই দেখা যায়।" },
      { q: "ডোমার, ডিমলা ও জলঢাকা কি অন্তর্ভুক্ত?", a: "হ্যাঁ — সার্চ ডিমান্ড থাকলে উপজেলা কভারেজ যুক্ত করা হয়।" },
    ],
  },
];

export const BN_LOCATION_SLUGS = new Set(BN_LOCATIONS.map((l) => l.slug));

export function findBnLocation(slug: string) {
  return BN_LOCATIONS.find((l) => l.slug === slug);
}

/** English source record for a Bangla city (geo, country, district codes). */
export function enLocation(slug: string): Location | undefined {
  return LOCATIONS.find((l) => l.slug === slug);
}

/**
 * Bangla copy for the programmable city × service pages. Keys mirror
 * `PROGRAMMABLE_SERVICE_SLUGS` so the EN and BN URL pairs line up exactly.
 */
export type BnServiceCopy = {
  title: string;
  tagline: string;
  angle: (city: string) => string;
  pillars: (city: string, areas: string[]) => string[];
  faqs: (city: string) => { q: string; a: string }[];
};

export const BN_SERVICES: Record<string, BnServiceCopy> = {
  "local-seo": {
    title: "লোকাল এসইও",
    tagline: "ম্যাপ প্যাকে আপনার শহরের শীর্ষে।",
    angle: (city) =>
      `${city}-র জন্য তৈরি Google Business Profile, ম্যাপ প্যাক র‍্যাংকিং, সাইটেশন ও রিভিউ গতি — স্থানীয় ক্রেতা যেন সবার আগে আপনাকেই পান।`,
    pillars: (city, areas) => [
      `${city}-র ঠিকানা ও সার্ভিস এরিয়া ধরে সম্পূর্ণ Google Business Profile পুনর্গঠন`,
      "বাংলাদেশ-সংশ্লিষ্ট ডিরেক্টরিতে NAP সাইটেশন পরিচ্ছন্ন করা",
      `${areas.slice(0, 3).join(", ")} সহ এলাকাভিত্তিক ল্যান্ডিং পেজ`,
      `${city}-র ক্রেতা আচরণ অনুযায়ী নীতিসম্মত রিভিউ সংগ্রহ`,
    ],
    faqs: (city) => [
      { q: `${city}-তে লোকাল এসইও-র খরচ কত?`, a: `${city}-র বেশিরভাগ ব্যবসার শুরু হয় মাসে ২৫,০০০–৬০,০০০ টাকার মধ্যে — ক্যাটাগরি কতটা প্রতিযোগিতামূলক এবং প্রোফাইল নতুন করে তৈরি করতে হবে কি না, তার ওপর নির্ভর করে। কাজ শুরুর আগেই পুরো স্কোপ লিখিতভাবে জানিয়ে দিই।` },
      { q: `${city}-র ম্যাপ প্যাকে আসতে কত দিন লাগে?`, a: `প্রোফাইল, ক্যাটাগরি, ছবি ও সাইটেশন ঠিক হয়ে গেলে সাধারণত ৩০–৬০ দিনে নড়াচড়া দেখা যায়। ক্লিনিক বা কোচিংয়ের মতো ভিড় বেশি ক্যাটাগরিতে ৯০ দিন লাগে, কারণ রিভিউ জমতে সময় লাগে।` },
      { q: `${city} শহরের বাইরের ব্যবসাও কি কভার হয়?`, a: `হ্যাঁ। আশপাশের উপজেলা ও পুরো সার্ভিস এরিয়া কভার করি — কাজ চলে রিমোটলি, মাসে একটি কল, আর প্রয়োজনে সরাসরি বৈঠক।` },
    ],
  },
  "technical-seo-audit": {
    title: "টেকনিক্যাল এসইও অডিট",
    tagline: "যে ভিত্তি ঠিক না হলে বাকি কিছুই কাজ করে না।",
    angle: (city) =>
      `${city}-র সাইটের জন্য ক্রল-ভিত্তিক ও Core Web Vitals কেন্দ্রিক অডিট — রেন্ডার, ইনডেক্সেশন ও স্কিমার যেসব সমস্যা চুপচাপ র‍্যাংকিং আটকে রাখে, সেগুলো বের করে আনা।`,
    pillars: (city) => [
      `${city}-র শীর্ষ প্রতিযোগীদের সঙ্গে তুলনা করে সম্পূর্ণ ক্রল ও লগ বিশ্লেষণ`,
      "বাংলাদেশের মোবাইল নেটওয়ার্ক ধরে Core Web Vitals রিপোর্ট",
      "স্কিমা, hreflang, canonical ও ইনডেক্সেশন পর্যালোচনা",
      "ডেভেলপারের জন্য অগ্রাধিকারসহ প্রস্তুত ফিক্স-টিকিট",
    ],
    faqs: (city) => [
      { q: `${city}-র একটি ব্যবসার টেকনিক্যাল অডিটে খরচ কত?`, a: `২০০-পয়েন্টের পূর্ণ অডিট নির্দিষ্ট ফি-তে হয়, পেজ সংখ্যা অনুযায়ী। ছোট সাইট দ্রুত শেষ হয়; ফিল্টারসহ ই-কমার্স ক্যাটালগে সময় বেশি লাগে, কারণ ক্রল বাজেট ও ইনডেক্সেশনের কাজই বড় অংশ।` },
      { q: "শুধু রিপোর্ট দেন, নাকি ঠিকও করে দেন?", a: "দুটোই সম্ভব। ডিফল্ট ডেলিভারেবল হলো ডেভেলপারের জন্য প্রস্তুত টিকিট। ডেভেলপার না থাকলে WordPress, Shopify, Webflow বা কাস্টম স্ট্যাকে আমি নিজেই ঠিক করে দিই।" },
      { q: "দেশের ধীরগতির মোবাইল নেটওয়ার্ক কি হিসাবে আসে?", a: "হ্যাঁ — মাপা হয় 4G-তে মাঝারি মানের অ্যান্ড্রয়েড ডিভাইস ধরে, কারণ আপনার ট্রাফিকের বড় অংশ ঠিক সেভাবেই আসে। ডেস্কটপ স্কোর আসল সমস্যাগুলো আড়াল করে রাখে।" },
    ],
  },
  "ecommerce-seo": {
    title: "ই-কমার্স এসইও",
    tagline: "ক্যাটাগরি পেজকে বিক্রির ইঞ্জিন বানানো।",
    angle: (city) =>
      `${city}-র ই-কমার্স ব্র্যান্ডের জন্য ক্যাটাগরি, কালেকশন ও প্রোডাক্ট এসইও — স্কিমা, ফেসেট নিয়ন্ত্রণ ও মার্চেন্ডাইজিং যা পেজকে ধারাবাহিক আয়ে রূপ দেয়।`,
    pillars: (city) => [
      `${city}-র ক্রেতা ইনটেন্ট ধরে ক্যাটাগরি ও কালেকশন কীওয়ার্ড ম্যাপ`,
      "Shopify / WooCommerce-এর জন্য ফেসেট, canonical ও ক্রল-বাজেট কৌশল",
      "রিচ রেজাল্টের জন্য প্রোডাক্ট, রিভিউ ও FAQ স্কিমা",
      `${city}-র দ্বিভাষিক ক্রেতার জন্য বাংলা ও ইংরেজি মার্চেন্ডাইজিং`,
    ],
    faqs: (city) => [
      { q: `${city}-র Shopify ও WooCommerce স্টোরের সঙ্গে কাজ করেন?`, a: `হ্যাঁ — বেশিরভাগ ক্লায়েন্ট Shopify, WooCommerce বা কাস্টম Laravel-এ থাকেন। কালেকশন কৌশল, স্কিমা ও ইন্টারনাল লিংকিং তিনটিতেই একই, শুধু বাস্তবায়নের ধরন বদলায়।` },
      { q: "প্রোডাক্ট পেজ বাংলায় হবে না ইংরেজিতে?", a: `সৎ উত্তর — সাধারণত দুটোই। ইংরেজি বাণিজ্যিক কীওয়ার্ড ধরে, বাংলা লং-টেইল ও ভয়েস সার্চ ধরে। কোন ক্যাটাগরিতে দুই ভাষা দরকার সেটি আগে ম্যাপ করি, সব কিছু অনুবাদ করি না।` },
      { q: "Daraz বা ফেসবুক শপের সঙ্গে পারা যাবে?", a: `বড় সাধারণ টার্মে মার্কেটপ্লেস এগিয়ে থাকবে। জেতার জায়গা হলো ক্যাটাগরির গভীরতা, বায়িং গাইড, ব্র্যান্ড টার্ম ও ${city}-কেন্দ্রিক ডেলিভারি সার্চ — লাভজনক অর্ডারগুলো সেখান থেকেই আসে।` },
    ],
  },
  "aeo-geo": {
    title: "AEO ও GEO (এআই সার্চ)",
    tagline: "এআই উত্তরে উদ্ধৃত হওয়ার কাজ।",
    angle: (city) =>
      `${city}-কেন্দ্রিক প্রশ্নে ChatGPT, Perplexity, Gemini ও Google AI Overviews-এ উদ্ধৃত হওয়া — এন্টিটি এসইও, কাঠামোবদ্ধ উত্তর ও llms.txt কৌশল।`,
    pillars: (city) => [
      `${city}, বাংলাদেশ ঘিরে ব্র্যান্ড ও এন্টিটি নলেজ-গ্রাফ গঠন`,
      "এলএলএম-বান্ধব কনটেন্ট ও স্কিমা মার্কআপ",
      "ChatGPT, Perplexity ও Gemini-তে মাসিক এআই দৃশ্যমানতা ট্র্যাকিং",
      `${city}-র সাইটের জন্য llms.txt ও এআই ক্রলার নীতিমালা`,
    ],
    faqs: (city) => [
      { q: `এআই অ্যাসিস্ট্যান্ট কি ${city}-র ব্যবসার নাম বলে?`, a: `ক্রমেই বলছে। "${city}-তে সেরা <সেবা>" ধরনের প্রশ্নের উত্তর ChatGPT, Perplexity ও Google AI Overviews তৈরি করে কাঠামোবদ্ধ, সূত্রসহ পেজ থেকে। ${city}-র খুব কম ব্র্যান্ড এটির জন্য কাজ করছে — সুযোগটা ঠিক সেখানেই।` },
      { q: `${city}-র ব্র্যান্ডের এআই সাইটেশন কীভাবে মাপেন?`, a: `আপনার সেবা, ক্যাটাগরি ও প্রতিযোগী তুলনা ধরে একটি নির্দিষ্ট প্রম্পট সেট প্রতি মাসে ChatGPT, Perplexity ও Gemini-তে চালাই, তারপর রিপোর্ট করি কোথায় আপনি এসেছেন, কোথায় প্রতিযোগী, আর কী বদলেছে।` },
      { q: `${city}-তে গুগলে ভালো র‍্যাংক করলে AEO দরকার আছে?`, a: `আছে। একই কনটেন্ট দুটোই আনে, কিন্তু এআই উত্তর ক্লিকটা কেড়ে নেয়। আপনি যদি আগে থেকেই র‍্যাংক করেন, তাহলে AEO মূলত নিজের পেজেই কাঠামো ও এন্টিটির কাজ — তাই সবচেয়ে সস্তা জয়গুলোর একটি।` },
    ],
  },
  "content-strategy": {
    title: "কনটেন্ট কৌশল",
    tagline: "কম লেখা, গভীর লেখা, ফল দেওয়া লেখা।",
    angle: (city) =>
      `${city}-র ব্র্যান্ডের জন্য টপিক্যাল ম্যাপ, SERP-ভিত্তিক ব্রিফ ও সম্পাদকীয় মানের প্রোডাকশন — যে কনটেন্ট র‍্যাংক করে, রূপান্তর ঘটায় এবং সময়মতো হালনাগাদ হয়।`,
    pillars: (city) => [
      `${city}-র প্রধান খাত ধরে টপিক্যাল ম্যাপ ও ক্লাস্টার পরিকল্পনা`,
      "SERP বিশ্লেষণভিত্তিক ব্রিফ ও সম্পাদকীয় নির্দেশিকা",
      "দীর্ঘ আর্টিকেল ও ল্যান্ডিং পেজ প্রোডাকশন",
      `${city}-র পুরোনো কনটেন্ট রিফ্রেশ করার ডিকে-রিকভারি প্রোগ্রাম`,
    ],
    faqs: (city) => [
      { q: `${city}-র একটি ব্যবসার আসলে কতটা কনটেন্ট দরকার?`, a: `সংখ্যা নয়, গভীরতা। বেশিরভাগ ${city}-র ব্র্যান্ডের জন্য দুই-তিনটি ক্লাস্টারে মাসে চার থেকে আটটি ভালোভাবে ব্রিফ করা লেখা প্রতিদিনের হালকা পোস্টের চেয়ে ভালো কাজ করে — রক্ষণাবেক্ষণেও সস্তা।` },
      { q: "ইংরেজির পাশাপাশি বাংলায়ও লেখেন?", a: "হ্যাঁ। বাংলা কনটেন্ট মেশিন-অনুবাদ নয়, সরাসরি বাংলায় লেখা হয় এবং hreflang দিয়ে ইংরেজি সংস্করণের সঙ্গে জোড়া লাগানো হয় — যাতে দুটোই র‍্যাংক করে, একে অপরের সঙ্গে প্রতিযোগিতা না করে।" },
      { q: "লেখেন কে — আপনি নাকি টিম?", a: `ব্রিফ, কাঠামো ও চূড়ান্ত সম্পাদনা আমার। ড্রাফটিংয়ে ছোট একটি যাচাই করা টিম থাকে, আর ${city}-র যে সার্চ ইনটেন্টের জন্য লেখা, তার বিপরীতে সিনিয়র পাস ছাড়া কিছুই প্রকাশ হয় না।` },
    ],
  },
};

export const BN_SERVICE_SLUGS = Object.keys(BN_SERVICES);

/** Every `/bn/seo-services/{city}/{service}` path, for the Bangla sitemap. */
export function bnCityServicePaths(): string[] {
  const out: string[] = [];
  for (const loc of BN_LOCATIONS) {
    for (const svc of BN_SERVICE_SLUGS) {
      out.push(`/bn/seo-services/${loc.slug}/${svc}`);
    }
  }
  return out;
}
